import { useState } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  ChartLineUp,
  TrendUp,
  TrendDown,
  Lightning,
  Target,
  Clock,
  Play,
  ArrowClockwise,
  DownloadSimple,
  ArrowUp,
  ArrowDown,
  Info
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subValue, trend, color }) => (
  <Card className="border-slate-200 rounded-sm shadow-none">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-sm ${color || 'bg-slate-100'}`}>
          <Icon size={18} className={trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-600'} weight="regular" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-body">{label}</p>
          <p className={`font-data text-xl font-medium tabular-nums ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-900'}`}>
            {value}
          </p>
          {subValue && (
            <p className="text-xs text-slate-500 font-body">{subValue}</p>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Equity Curve Chart
const EquityCurve = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const chartData = data.map((point, index) => ({
    index,
    equity: point.equity
  }));
  
  const startEquity = chartData[0]?.equity || 100;
  const endEquity = chartData[chartData.length - 1]?.equity || 100;
  const isProfit = endEquity >= startEquity;
  
  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
          <XAxis dataKey="index" hide />
          <YAxis domain={['auto', 'auto']} hide />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'white', 
              border: '1px solid #e2e8f0',
              borderRadius: '2px',
              fontSize: '12px',
              fontFamily: 'JetBrains Mono'
            }}
            formatter={(value) => [`$${value.toFixed(2)}`, 'Équité']}
          />
          <Line 
            type="linear" 
            dataKey="equity" 
            stroke={isProfit ? "#16a34a" : "#dc2626"} 
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Score Distribution Chart
const ScoreDistribution = ({ data }) => {
  if (!data || Object.keys(data).length === 0) return null;
  
  const chartData = Object.entries(data)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([range, count]) => ({
      range,
      count
    }));
  
  return (
    <div className="h-40 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
          <XAxis 
            dataKey="range" 
            tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
            angle={-45}
            textAnchor="end"
          />
          <YAxis hide />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'white', 
              border: '1px solid #e2e8f0',
              borderRadius: '2px',
              fontSize: '12px'
            }}
          />
          <Bar dataKey="count" radius={[2, 2, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={parseInt(entry.range) >= 80 ? '#16a34a' : parseInt(entry.range) >= 70 ? '#2563eb' : '#64748b'} 
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Helper to format prices precisely (small caps need more decimals)
const formatPrice = (p) => {
  if (p === null || p === undefined) return '-';
  if (p >= 1000) return `$${p.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  if (p >= 1) return `$${p.toFixed(4)}`;
  if (p >= 0.01) return `$${p.toFixed(6)}`;
  return `$${p.toFixed(8)}`;
};

const EXIT_REASON_LABEL = {
  take_profit: { label: "Take Profit atteint", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  stop_loss: { label: "Stop Loss touché", color: "text-red-700 bg-red-50 border-red-200" },
  time_exit: { label: "Sortie temporelle (8j)", color: "text-amber-700 bg-amber-50 border-amber-200" },
};

// Trade Detail Dialog - opens when a trade row is clicked
const TradeDetailDialog = ({ trade, open, onClose }) => {
  if (!trade) return null;
  const isLong = trade.side === "long";
  const isWin = trade.trade_type === "win";
  const reasonInfo = EXIT_REASON_LABEL[trade.exit_reason] || { label: trade.exit_reason || "-", color: "text-slate-600 bg-slate-50" };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="trade-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="font-heading text-2xl font-bold">{trade.symbol}</span>
            <Badge
              className={`font-data text-xs ${isLong ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
              data-testid="dialog-side"
            >
              {isLong ? <ArrowUp size={12} weight="bold" className="mr-1 inline" /> : <ArrowDown size={12} weight="bold" className="mr-1 inline" />}
              {trade.side?.toUpperCase()}
            </Badge>
            <Badge
              className={`font-data text-xs ${isWin ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}
              data-testid="dialog-result"
            >
              {isWin ? "GAGNÉ" : "PERDU"}
            </Badge>
            <Badge variant="outline" className={`font-data text-xs ${reasonInfo.color}`} data-testid="dialog-exit-reason">
              {reasonInfo.label}
            </Badge>
          </div>
          <DialogTitle className="sr-only">Détails du trade {trade.symbol}</DialogTitle>
          <DialogDescription className="font-body text-xs text-slate-400">
            Score d'entrée: {trade.score_at_entry} • Tenu pendant {trade.holding_days || '-'} jour(s)
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-4">
          {/* PRICES */}
          <div className="col-span-2 grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-sm p-3 border border-slate-100">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-body mb-1">Entrée</p>
              <p className="font-data text-base font-medium text-slate-900 tabular-nums">{formatPrice(trade.entry_price)}</p>
              <p className="text-xs text-slate-400 mt-1">{new Date(trade.entry_date).toLocaleDateString('fr-FR')}</p>
            </div>
            <div className="bg-slate-50 rounded-sm p-3 border border-slate-100">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-body mb-1">Sortie</p>
              <p className="font-data text-base font-medium text-slate-900 tabular-nums">{formatPrice(trade.exit_price)}</p>
              <p className="text-xs text-slate-400 mt-1">{new Date(trade.exit_date).toLocaleDateString('fr-FR')}</p>
            </div>
            <div className={`rounded-sm p-3 border ${isWin ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-body mb-1">P&L</p>
              <p className={`font-data text-base font-medium tabular-nums ${isWin ? 'text-emerald-700' : 'text-red-700'}`} data-testid="dialog-pnl">
                {trade.profit_percent >= 0 ? '+' : ''}{trade.profit_percent}%
              </p>
              <p className="text-xs text-slate-400 mt-1">R réalisé: {trade.risk_reward_achieved}</p>
            </div>
          </div>

          {/* SL / TP */}
          <div className="bg-red-50 rounded-sm p-3 border border-red-100">
            <p className="text-xs uppercase tracking-wider text-red-500 font-body mb-1 flex items-center gap-1">
              <Target size={12} weight="bold" /> Stop Loss
            </p>
            <p className="font-data text-base font-medium text-red-700 tabular-nums">{formatPrice(trade.stop_loss)}</p>
            <p className="text-xs text-red-500 mt-1">
              {isLong ? '-' : '+'}{Math.abs(((trade.stop_loss - trade.entry_price) / trade.entry_price) * 100).toFixed(2)}% depuis entrée
            </p>
          </div>
          <div className="bg-emerald-50 rounded-sm p-3 border border-emerald-100">
            <p className="text-xs uppercase tracking-wider text-emerald-500 font-body mb-1 flex items-center gap-1">
              <Target size={12} weight="bold" /> Take Profit
            </p>
            <p className="font-data text-base font-medium text-emerald-700 tabular-nums">{formatPrice(trade.take_profit)}</p>
            <p className="text-xs text-emerald-500 mt-1">
              {isLong ? '+' : '-'}{Math.abs(((trade.take_profit - trade.entry_price) / trade.entry_price) * 100).toFixed(2)}% depuis entrée
            </p>
          </div>

          {/* INDICATORS AT ENTRY */}
          <div className="col-span-2">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-body mb-2 font-bold">Indicateurs à l'entrée</p>
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-sm p-2 border border-slate-100">
                <p className="text-xs text-slate-400 font-body">RSI</p>
                <p className={`font-data text-sm font-medium tabular-nums ${
                  (trade.rsi_at_entry < 30) ? 'text-emerald-700' :
                  (trade.rsi_at_entry > 70) ? 'text-red-700' : 'text-slate-900'
                }`} data-testid="dialog-rsi">
                  {trade.rsi_at_entry !== null && trade.rsi_at_entry !== undefined ? trade.rsi_at_entry : '-'}
                </p>
              </div>
              <div className="bg-slate-50 rounded-sm p-2 border border-slate-100">
                <p className="text-xs text-slate-400 font-body">MACD</p>
                <p className={`font-data text-sm font-medium tabular-nums ${
                  trade.macd_at_entry > 0 ? 'text-emerald-700' : 'text-red-700'
                }`}>
                  {trade.macd_at_entry !== null && trade.macd_at_entry !== undefined ? trade.macd_at_entry : '-'}
                </p>
              </div>
              <div className="bg-slate-50 rounded-sm p-2 border border-slate-100">
                <p className="text-xs text-slate-400 font-body">MA20</p>
                <p className="font-data text-sm font-medium tabular-nums text-slate-900">
                  {trade.ma20_at_entry !== null && trade.ma20_at_entry !== undefined ? formatPrice(trade.ma20_at_entry) : '-'}
                </p>
              </div>
              <div className="bg-slate-50 rounded-sm p-2 border border-slate-100">
                <p className="text-xs text-slate-400 font-body">MA50</p>
                <p className="font-data text-sm font-medium tabular-nums text-slate-900">
                  {trade.ma50_at_entry !== null && trade.ma50_at_entry !== undefined ? formatPrice(trade.ma50_at_entry) : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Trade Row Component
const TradeRow = ({ trade, onClick }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <TableRow
      data-testid={`backtest-trade-${trade.symbol}`}
      className="cursor-pointer hover:bg-slate-50 transition-colors"
      onClick={() => onClick(trade)}
    >
      <TableCell>
        <span className="font-heading font-bold text-slate-900">{trade.symbol}</span>
      </TableCell>
      <TableCell>
        {trade.side ? (
          <span className={`flex items-center gap-1 text-xs font-bold ${trade.side === 'long' ? 'text-emerald-600' : 'text-red-500'}`}>
            {trade.side === 'long' ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
            {trade.side.toUpperCase()}
          </span>
        ) : <span className="text-slate-300">-</span>}
      </TableCell>
      <TableCell className="font-data text-xs tabular-nums text-slate-500">
        {formatDate(trade.entry_date)}
      </TableCell>
      <TableCell className="font-data tabular-nums">{formatPrice(trade.entry_price)}</TableCell>
      <TableCell className="font-data tabular-nums">{formatPrice(trade.exit_price)}</TableCell>
      <TableCell>
        <Badge 
          className={`font-data text-xs ${
            trade.score_at_entry >= 85 ? 'bg-emerald-100 text-emerald-700' :
            trade.score_at_entry >= 70 ? 'bg-blue-100 text-blue-700' :
            'bg-slate-100 text-slate-700'
          }`}
        >
          {trade.score_at_entry}
        </Badge>
      </TableCell>
      <TableCell>
        <span className={`font-data tabular-nums font-medium ${
          trade.profit_percent >= 0 ? 'text-emerald-600' : 'text-red-600'
        }`}>
          {trade.profit_percent >= 0 ? '+' : ''}{trade.profit_percent.toFixed(2)}%
        </span>
      </TableCell>
      <TableCell>
        <Badge 
          variant="outline"
          className={trade.trade_type === 'win' ? 'border-emerald-300 text-emerald-700' : 'border-red-300 text-red-700'}
        >
          {trade.trade_type === 'win' ? 'Gagné' : 'Perdu'}
        </Badge>
      </TableCell>
      <TableCell>
        <Info size={14} className="text-slate-300 hover:text-blue-500" />
      </TableCell>
    </TableRow>
  );
};

export default function Backtesting() {
  const [config, setConfig] = useState({
    min_score: 70,
    days: 30,
    risk_per_trade: 2.0,
    take_profit_ratio: 2.0,
    category: "crypto",
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);

  const runBacktest = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/backtest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (!response.ok) throw new Error("Backtest failed");
      
      const data = await response.json();
      setResults(data);
      toast.success(`Backtest terminé: ${data.total_trades} trades simulés`);
    } catch (error) {
      console.error("Error running backtest:", error);
      toast.error("Erreur lors du backtest");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="backtesting-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Backtesting</h1>
          <p className="text-sm text-slate-500 font-body mt-1">
            Validez la performance du système de scoring sur données historiques
          </p>
        </div>
        <div className="flex gap-2">
          {results && (
            <Button
              variant="outline"
              onClick={() => {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = `${API}/backtest/export`;
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'config';
                input.value = JSON.stringify(config);
                form.appendChild(input);
                // Use fetch for POST export
                fetch(`${API}/backtest/export`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(config)
                }).then(r => r.blob()).then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `backtest_${new Date().toISOString().slice(0,10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Export CSV telecharge!");
                }).catch(() => toast.error("Erreur export"));
              }}
              className="rounded-sm"
              data-testid="export-backtest-btn"
            >
              <DownloadSimple size={16} className="mr-2" />
              Export CSV
            </Button>
          )}
          <Button 
            onClick={runBacktest}
            disabled={loading}
            className="rounded-sm"
            data-testid="run-backtest-btn"
          >
            {loading ? (
              <>
                <ArrowClockwise size={16} className="mr-2 animate-spin" />
                Simulation...
              </>
            ) : (
              <>
                <Play size={16} className="mr-2" weight="fill" />
                Lancer le Backtest
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Configuration */}
      <Card className="border-slate-200 rounded-sm shadow-none mb-6">
        <CardHeader>
          <CardTitle className="font-heading text-base">Configuration</CardTitle>
          <CardDescription className="font-body">
            Ajustez les paramètres de simulation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="space-y-3">
              <Label className="font-body">Catégorie</Label>
              <Select
                value={config.category}
                onValueChange={(value) => setConfig(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crypto">🪙 Crypto (14 actifs)</SelectItem>
                  <SelectItem value="metals">🥇 Métaux précieux</SelectItem>
                  <SelectItem value="indices">📈 Indices (SP500, Nasdaq, Dow, DAX)</SelectItem>
                  <SelectItem value="all">🌐 Tout combiné</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="font-body">Score Minimum</Label>
                <span className="font-data text-sm font-medium text-slate-900">{config.min_score}</span>
              </div>
              <Slider
                value={[config.min_score]}
                onValueChange={(value) => setConfig(prev => ({ ...prev, min_score: value[0] }))}
                max={95}
                min={60}
                step={5}
                className="w-full"
                data-testid="min-score-slider"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>60</span>
                <span>95</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <Label className="font-body">Période (jours)</Label>
              <Select 
                value={config.days.toString()}
                onValueChange={(value) => setConfig(prev => ({ ...prev, days: parseInt(value), date_from: "", date_to: "" }))}
                disabled={!!(config.date_from && config.date_to)}
              >
                <SelectTrigger className="rounded-sm" data-testid="days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="14">14 jours</SelectItem>
                  <SelectItem value="30">30 jours</SelectItem>
                  <SelectItem value="60">60 jours</SelectItem>
                  <SelectItem value="90">90 jours</SelectItem>
                </SelectContent>
              </Select>
              {config.date_from && config.date_to && (
                <p className="text-[10px] text-amber-600 font-body">Désactivé : plage personnalisée active</p>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="font-body">Risque par Trade</Label>
                <span className="font-data text-sm font-medium text-slate-900">{config.risk_per_trade}%</span>
              </div>
              <Slider
                value={[config.risk_per_trade]}
                onValueChange={(value) => setConfig(prev => ({ ...prev, risk_per_trade: value[0] }))}
                max={5}
                min={0.5}
                step={0.5}
                className="w-full"
                data-testid="risk-slider"
              />
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="font-body">Ratio R:R</Label>
                <span className="font-data text-sm font-medium text-slate-900">{config.take_profit_ratio}x</span>
              </div>
              <Slider
                value={[config.take_profit_ratio]}
                onValueChange={(value) => setConfig(prev => ({ ...prev, take_profit_ratio: value[0] }))}
                max={5}
                min={1}
                step={0.5}
                className="w-full"
                data-testid="rr-slider"
              />
            </div>
          </div>

          {/* Custom date range (optional, overrides "Période jours" if both set) */}
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px] max-w-xs">
                <Label className="font-body text-xs uppercase tracking-wider text-slate-500">
                  Plage personnalisée — Du
                </Label>
                <Input
                  type="date"
                  value={config.date_from || ""}
                  onChange={(e) => setConfig(prev => ({ ...prev, date_from: e.target.value }))}
                  className="rounded-sm font-data mt-1"
                  data-testid="date-from"
                />
              </div>
              <div className="flex-1 min-w-[180px] max-w-xs">
                <Label className="font-body text-xs uppercase tracking-wider text-slate-500">
                  Au
                </Label>
                <Input
                  type="date"
                  value={config.date_to || ""}
                  onChange={(e) => setConfig(prev => ({ ...prev, date_to: e.target.value }))}
                  className="rounded-sm font-data mt-1"
                  data-testid="date-to"
                />
              </div>
              {(config.date_from || config.date_to) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-sm mt-5 text-xs"
                  onClick={() => setConfig(prev => ({ ...prev, date_from: "", date_to: "" }))}
                  data-testid="clear-dates"
                >
                  <ArrowClockwise size={12} className="mr-1" />
                  Réinitialiser dates
                </Button>
              )}
              <p className="text-xs text-slate-400 font-body mt-5 flex-1">
                {config.date_from && config.date_to
                  ? `📅 Backtest sur la plage exacte ${config.date_from} → ${config.date_to}`
                  : "Optionnel : remplissez les 2 dates pour figer une période exacte (ex : Q1 2026)"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      )}

      {results && !loading && (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <StatsCard 
              icon={ChartLineUp}
              label="Total Trades"
              value={results.total_trades}
              subValue={`${results.winning_trades} gagnés / ${results.losing_trades} perdus`}
            />
            <StatsCard 
              icon={Target}
              label="Win Rate"
              value={`${results.win_rate}%`}
              trend={results.win_rate >= 50 ? 'up' : 'down'}
            />
            <StatsCard 
              icon={results.total_profit_percent >= 0 ? TrendUp : TrendDown}
              label="Profit Equité"
              value={`${results.total_profit_percent >= 0 ? '+' : ''}${results.total_profit_percent}%`}
              subValue={`avec risque ${config.risk_per_trade}% / trade`}
              trend={results.total_profit_percent >= 0 ? 'up' : 'down'}
            />
            <StatsCard 
              icon={Lightning}
              label="Profit Factor"
              value={results.profit_factor.toFixed(2)}
              subValue={results.profit_factor >= 1.5 ? "Excellent" : results.profit_factor >= 1 ? "Acceptable" : "À améliorer"}
              trend={results.profit_factor >= 1.5 ? 'up' : results.profit_factor >= 1 ? 'neutral' : 'down'}
            />
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Gain Moyen</p>
                <p className="font-data text-lg font-medium text-emerald-600 tabular-nums">+{results.average_win}%</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Perte Moyenne</p>
                <p className="font-data text-lg font-medium text-red-600 tabular-nums">-{results.average_loss}%</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Max Drawdown</p>
                <p className="font-data text-lg font-medium text-red-600 tabular-nums">-{results.max_drawdown}%</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Meilleur / Pire</p>
                <p className="font-data text-sm tabular-nums">
                  <span className="text-emerald-600">+{results.best_trade}%</span>
                  {" / "}
                  <span className="text-red-600">{results.worst_trade}%</span>
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
                  Courbe d'Équité
                </CardTitle>
              </CardHeader>
              <CardContent>
                <EquityCurve data={results.equity_curve} />
              </CardContent>
            </Card>
            
            <Card className="border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
                  Distribution par Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScoreDistribution data={results.score_distribution} />
              </CardContent>
            </Card>
          </div>

          {/* Trades Table */}
          <Card className="border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
                Derniers Trades Simulés
              </CardTitle>
              <CardDescription className="font-body text-xs">
                Cliquez sur une ligne pour voir les détails complets
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {results.trades.length === 0 ? (
                <div className="text-center py-12">
                  <Clock size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 font-body">Aucun trade simulé</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-body text-xs uppercase tracking-wider">Symbole</TableHead>
                      <TableHead className="font-body text-xs uppercase tracking-wider">Côté</TableHead>
                      <TableHead className="font-body text-xs uppercase tracking-wider">Date</TableHead>
                      <TableHead className="font-body text-xs uppercase tracking-wider">Entrée</TableHead>
                      <TableHead className="font-body text-xs uppercase tracking-wider">Sortie</TableHead>
                      <TableHead className="font-body text-xs uppercase tracking-wider">Score</TableHead>
                      <TableHead className="font-body text-xs uppercase tracking-wider">P&L</TableHead>
                      <TableHead className="font-body text-xs uppercase tracking-wider">Résultat</TableHead>
                      <TableHead className="font-body text-xs uppercase tracking-wider w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.trades.slice(0, 20).map((trade, index) => (
                      <TradeRow
                        key={`${trade.symbol}-${index}`}
                        trade={trade}
                        onClick={setSelectedTrade}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Trade detail popup */}
          <TradeDetailDialog
            trade={selectedTrade}
            open={!!selectedTrade}
            onClose={() => setSelectedTrade(null)}
          />
        </>
      )}

      {/* No results yet */}
      {!results && !loading && (
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="py-16">
            <div className="text-center">
              <ChartLineUp size={64} className="mx-auto text-slate-300 mb-4" />
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Prêt pour le Backtest</h3>
              <p className="text-sm text-slate-500 font-body max-w-md mx-auto">
                Configurez les paramètres ci-dessus et lancez une simulation pour évaluer 
                la performance historique du système de scoring.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
